import { useCallback, useEffect, useMemo } from "react";
import useSWR from "swr";
import { populationsApi, usersApi } from "@/lib/api";
import type { FeaturedTwin } from "@/lib/api";
import {
  fetchAllReadyAudiences,
  fetchFeaturedTwinPoolForAudiences,
  selectBookmarkedAudiencesForHomepage,
  selectFeaturedTwinsFromBookmarkedAudiences,
  toFeaturedTwinUpdatePayload,
} from "@/lib/homepage-personalization";
import { onFeaturedTwinsEnriched } from "@/lib/services/websocket";

const SWR_KEY = "featured-twins";
const MAX_TWINS = 8;

export interface UseFeaturedTwinsResult {
  twins: FeaturedTwin[];
  isLoading: boolean;
  pinnedTwins: FeaturedTwin[];
  autoFilledTwins: FeaturedTwin[];
  addTwin: (twin: FeaturedTwin) => Promise<void>;
  removeTwin: (personaId: string) => Promise<void>;
  reorder: (orderedTwins: FeaturedTwin[]) => Promise<void>;
  togglePin: (personaId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export interface UseFeaturedTwinsOptions {
  enabled?: boolean;
}

/**
 * Shared hook for user-managed featured twins.
 *
 * Onboarding may seed the first list, but after that an empty list is a valid
 * user choice and must not be auto-filled from targeting preferences.
 */
export function useFeaturedTwins(options: UseFeaturedTwinsOptions = {}): UseFeaturedTwinsResult {
  const isEnabled = options.enabled ?? true;
  const { data, isLoading, mutate } = useSWR<FeaturedTwin[]>(
    isEnabled ? SWR_KEY : null,
    async () => {
      const [userTwins, profile] = await Promise.all([
        populationsApi.getUserFeaturedTwins(),
        usersApi.getProfile().catch(() => null),
      ]);
      if (userTwins.length > 0) {
        return userTwins;
      }

      // `featuredTwins: null` means never initialized. `[]` means the user
      // intentionally cleared the list, so do not auto-refill it.
      if (profile && Array.isArray(profile.featuredTwins)) {
        return [];
      }

      const [audiences, targeting] = await Promise.all([
        fetchAllReadyAudiences().catch(() => []),
        usersApi.getTargetingPreferences().catch(() => null),
      ]);
      const bookmarkedAudiences = selectBookmarkedAudiencesForHomepage(
        audiences,
        targeting,
        audiences.length,
      );
      if (bookmarkedAudiences.length === 0) {
        return [];
      }

      const marketplaceTwins = await fetchFeaturedTwinPoolForAudiences(bookmarkedAudiences);
      const selected = selectFeaturedTwinsFromBookmarkedAudiences({
        bookmarkedAudiences,
        marketplaceTwins,
      });
      if (selected.length > 0) {
        await usersApi.updateFeaturedTwins(selected.map(toFeaturedTwinUpdatePayload));
      }
      return selected;
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );

  // Listen for Phase 2 (summary) enrichment pushed by the server so the UI
  // shows the bio/tagline without requiring a manual page refresh.
  useEffect(() => {
    if (!isEnabled) return;
    const off = onFeaturedTwinsEnriched(({ twins: enrichedTwins }) => {
      mutate(enrichedTwins as FeaturedTwin[], { revalidate: false });
    });
    return off;
  }, [isEnabled, mutate]);

  const twins = useMemo(() => data ?? [], [data]);

  const pinnedTwins = useMemo(
    () => twins.filter((t) => t.pinned === true),
    [twins],
  );

  const autoFilledTwins = useMemo(
    () => twins.filter((t) => !t.pinned),
    [twins],
  );

  const persistTwins = useCallback(
    async (updated: FeaturedTwin[]) => {
      if (!isEnabled) return;
      const payload = updated.map(toFeaturedTwinUpdatePayload);
      const serverTwins = await usersApi.updateFeaturedTwins(payload);
      await mutate(serverTwins, { revalidate: false });
    },
    [isEnabled, mutate],
  );

  const addTwin = useCallback(
    async (twin: FeaturedTwin) => {
      const current = twins
        .filter((t) => t.personaId !== twin.personaId)
        .slice(0, MAX_TWINS - 1);
      const newTwin = { ...twin, pinned: true };
      await persistTwins([...current, newTwin]);
    },
    [twins, persistTwins],
  );

  const removeTwin = useCallback(
    async (personaId: string) => {
      const updated = twins.filter((t) => t.personaId !== personaId);
      await persistTwins(updated);
    },
    [twins, persistTwins],
  );

  const reorder = useCallback(
    async (orderedTwins: FeaturedTwin[]) => {
      await persistTwins(orderedTwins);
    },
    [persistTwins],
  );

  const togglePin = useCallback(
    async (personaId: string) => {
      const updated = twins.map((t) =>
        t.personaId === personaId ? { ...t, pinned: !t.pinned } : t,
      );
      await persistTwins(updated);
    },
    [twins, persistTwins],
  );

  const refresh = useCallback(async () => {
    if (!isEnabled) return;
    await mutate();
  }, [isEnabled, mutate]);

  return {
    twins,
    isLoading,
    pinnedTwins,
    autoFilledTwins,
    addTwin,
    removeTwin,
    reorder,
    togglePin,
    refresh,
  };
}

export function getDistinctFeaturedTwins(twins: FeaturedTwin[]): FeaturedTwin[] {
  const seenPersonaIds = new Set<string>();
  const seenImageKeys = new Set<string>();

  return twins.filter((twin) => {
    if (seenPersonaIds.has(twin.personaId)) {
      return false;
    }

    const imageKeys = getFeaturedTwinImageKeys(twin);
    if (imageKeys.some((key) => seenImageKeys.has(key))) {
      return false;
    }

    seenPersonaIds.add(twin.personaId);
    imageKeys.forEach((key) => seenImageKeys.add(key));
    return true;
  });
}

export function shouldPersistFeaturedTwins(
  current: FeaturedTwin[],
  next: FeaturedTwin[],
): boolean {
  if (current.length !== next.length) return true;

  return current.some((currentTwin, index) => {
    const nextTwin = next[index];
    if (!nextTwin) return true;
    return (
      currentTwin.personaId !== nextTwin.personaId ||
      currentTwin.populationId !== nextTwin.populationId ||
      currentTwin.pinned !== nextTwin.pinned ||
      currentTwin.imageId !== nextTwin.imageId ||
      currentTwin.imageUrl !== nextTwin.imageUrl ||
      currentTwin.personaName !== nextTwin.personaName ||
      currentTwin.personaTagline !== nextTwin.personaTagline
    );
  });
}

function getFeaturedTwinImageKeys(twin: FeaturedTwin): Array<string> {
  const keys: Array<string> = [];

  if (isPresentValue(twin.imageId)) {
    keys.push(`id:${twin.imageId.trim()}`);
  }

  if (isPresentValue(twin.imageUrl)) {
    keys.push(`url:${normalizeImageUrlForDedupe(twin.imageUrl)}`);
  }

  return keys;
}

function normalizeImageUrlForDedupe(imageUrl: string): string {
  const trimmed = imageUrl.trim();
  try {
    const url = new URL(trimmed, 'http://local');
    return `${url.origin}${url.pathname}`;
  } catch {
    return trimmed.split(/[?#]/)[0];
  }
}

function isPresentValue(value: string | null | undefined): value is string {
  const normalized = value?.trim();
  return Boolean(
    normalized &&
      normalized !== 'null' &&
      normalized !== 'undefined',
  );
}

